import React, { useCallback, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";
import AttendanceHeader from "./AttendanceHeader";
import Text from "@components/CustomText";
import {
  ATTENDANCE_ROLES,
  formatAttendanceDate,
  formatClockTime,
  formatShiftDuration,
  todayIST,
} from "@utils/attendance";
import { formatSummary, shiftMonth } from "@utils/attendanceCalendar";
import { ATTENDANCE_COLORS as C } from "./constants";
import HistoryCalendar from "./HistoryCalendar";
import { useAttendanceHistory } from "./useAttendanceHistory";
import type { AttendanceHistoryParams, HistoryDay } from "./types";

interface Props {
  navigation: { goBack: () => void };
  route: { name: string; params: AttendanceHistoryParams };
}

const EMPTY = "—";

const DayDetail = ({ day }: { day: HistoryDay }) => (
  <View style={styles.detail} testID="attendance-history-detail">
    <Text style={styles.detailTitle}>{formatAttendanceDate(day.date)}</Text>
    <DetailRow label="In Time" value={formatClockTime(day.in_at) || EMPTY} />
    <DetailRow label="Out Time" value={formatClockTime(day.out_at) || EMPTY} />
    <DetailRow
      label="Duration"
      value={formatShiftDuration(day.in_at ?? "", day.out_at ?? "") || EMPTY}
    />
  </View>
);

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <View style={styles.detailRow}>
    <Text style={styles.detailLabel}>{label}</Text>
    <Text style={styles.detailValue}>{value}</Text>
  </View>
);

const AttendanceHistory = ({ navigation, route }: Props) => {
  const { identity } = route.params;
  const { month, setMonth, data, loading, error, retry } = useAttendanceHistory(
    route.params,
  );
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const todayDate = todayIST();
  const isJr = identity.role === ATTENDANCE_ROLES.JR_EXECUTIVE;
  const subject = data?.subject ?? identity.subject;

  const goToMonth = useCallback(
    (delta: number) => {
      setSelectedDate(null);
      setMonth((m) => shiftMonth(m, delta));
    },
    [setMonth],
  );
  const handlePrev = useCallback(() => goToMonth(-1), [goToMonth]);
  const handleNext = useCallback(() => goToMonth(1), [goToMonth]);

  const selectedDay = useMemo(
    () => data?.days.find((d) => d.date === selectedDate) ?? null,
    [data, selectedDate],
  );

  return (
    <View style={styles.container}>
      <AttendanceHeader
        headertitle="Attendance History"
        navigation={navigation}
        route={route}
      />
      <View style={styles.identity} testID="attendance-history-identity">
        <Text style={styles.identityLabel}>
          {subject.display_label || (isJr ? "CP_DB ID" : "EMPLOYEE CODE")}
        </Text>
        <Text style={styles.identityValue} selectable>
          {subject.display_id}
        </Text>
        {subject.name ? (
          <Text style={styles.identityName}>{subject.name}</Text>
        ) : null}
      </View>
      <ScrollView contentContainerStyle={styles.content}>
        <HistoryCalendar
          month={month}
          days={data?.days ?? []}
          todayDate={todayDate}
          selectedDate={selectedDate}
          onSelectDate={setSelectedDate}
          onPrevMonth={handlePrev}
          onNextMonth={handleNext}
        />
        {loading ? (
          <ActivityIndicator
            color={C.ink}
            style={styles.spinner}
            testID="attendance-history-loading"
          />
        ) : null}
        {!loading && error ? (
          <View style={styles.errorBox} testID="attendance-history-error">
            <Text style={styles.errorText}>{error}</Text>
            <Pressable
              onPress={retry}
              style={styles.retry}
              accessibilityRole="button"
              accessibilityLabel="Retry"
            >
              <Text style={styles.retryText}>Retry</Text>
            </Pressable>
          </View>
        ) : null}
        {!loading && data ? (
          <Text style={styles.summary} testID="attendance-history-summary">
            {formatSummary(data.days)}
          </Text>
        ) : null}
        {!loading && selectedDay ? <DayDetail day={selectedDay} /> : null}
      </ScrollView>
    </View>
  );
};

export default AttendanceHistory;

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: C.screenBg },
  identity: {
    backgroundColor: C.white,
    paddingHorizontal: 18,
    paddingBottom: 14,
  },
  identityLabel: { color: C.label, fontSize: 10, letterSpacing: 1 },
  identityValue: {
    color: C.ink,
    fontSize: 16,
    fontWeight: "700",
    marginTop: 2,
  },
  identityName: { color: C.muted, fontSize: 13, marginTop: 2 },
  content: { padding: 16, gap: 14 },
  spinner: { paddingVertical: 12 },
  summary: {
    color: C.ink,
    fontSize: 14,
    fontWeight: "600",
    textAlign: "center",
  },
  errorBox: {
    backgroundColor: C.white,
    borderRadius: 14,
    padding: 16,
    alignItems: "center",
    gap: 10,
  },
  errorText: { color: C.pending, fontSize: 14, textAlign: "center" },
  retry: {
    borderWidth: 1,
    borderColor: C.ink,
    borderRadius: 10,
    paddingVertical: 8,
    paddingHorizontal: 22,
  },
  retryText: { color: C.ink, fontSize: 14, fontWeight: "600" },
  detail: {
    backgroundColor: C.white,
    borderRadius: 14,
    paddingVertical: 14,
    paddingHorizontal: 18,
    gap: 8,
  },
  detailTitle: { color: C.ink, fontSize: 15, fontWeight: "700" },
  detailRow: { flexDirection: "row", justifyContent: "space-between" },
  detailLabel: { color: C.muted, fontSize: 14 },
  detailValue: { color: C.ink, fontSize: 14, fontWeight: "700" },
});
